import { useEffect, useState, useCallback } from "react";
import { api, fmtDate, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Btn, Input, Select, Modal, ConfirmDialog } from "@/components/Kit";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Download, Eraser, AlertTriangle, Power } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { usePagination, Pagination } from "@/components/Pagination";

const DEFAULT_PREFIXES = { COT: "C-", REM: "remisión", OT: "OT-", FAC: "Factura" };


const DOC_ACTIONS = ["view", "create", "edit", "convert", "cancel"];
const CATALOG_ACTIONS = ["view", "create", "edit", "cancel"];
const VIEW_ONLY = ["view"];

const ACTION_COLS = [
  { key: "view", label: "Consultar" },
  { key: "create", label: "Crear" },
  { key: "edit", label: "Editar" },
  { key: "convert", label: "Convertir" },
  { key: "cancel", label: "Cancelar" },
];

const MODULES = [
  { key: "dashboard", label: "Panel", actions: VIEW_ONLY },
  { key: "cotizaciones", label: "Cotizaciones", actions: DOC_ACTIONS },
  { key: "remisiones", label: "Remisiones", actions: DOC_ACTIONS },
  { key: "ordenes", label: "Órdenes de Trabajo", actions: DOC_ACTIONS },
  { key: "facturas", label: "Facturación", actions: DOC_ACTIONS },
  { key: "cobranza", label: "Cobranza", actions: CATALOG_ACTIONS },
  { key: "productos", label: "Productos", actions: CATALOG_ACTIONS },
  { key: "clientes", label: "Clientes", actions: CATALOG_ACTIONS },
  { key: "configuracion", label: "Configuración", actions: VIEW_ONLY },
];
const ROLE_LABELS = { admin: "Administrador", viewer: "Solo consulta", ventas: "Ventas", produccion: "Producción", cobranza: "Cobranza", custom: "Personalizado" };

// Convierte permisos heredados (lista de módulos) al formato de matriz { módulo: [acciones] }
function toActionMap(p) {
  if (p && !Array.isArray(p) && typeof p === "object") {
    const out = {};
    for (const k of Object.keys(p)) out[k] = [...(p[k] || [])];
    return out;
  }
  const out = {};
  (p || []).forEach((m) => {
    const mod = MODULES.find((x) => x.key === m);
    out[m] = mod ? [...mod.actions] : ["view"];
  });
  return out;
}

export default function Configuracion() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("empresa");
  const tabs = [["empresa", "Empresa"], ["folios", "Folios"], ["panel", "Panel"], ["bancos", "Bancos / Datos de pago"], ["agentes", "Agentes"], ["usuarios", "Usuarios"], ["bitacora", "Bitácora / Actividad"]];
  if (isAdmin) tabs.push(["mantenimiento", "Mantenimiento"]);
  return (
    <>
      <PageHeader overline="Ajustes del sistema" title="Configuración" />
      <div className="px-10 pt-6">
        <div className="flex gap-1 border-b border-[#E3E8EE]">
          {tabs.map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`config-tab-${k}`}
              className={`px-4 py-3 text-sm uppercase tracking-wider border-b-2 -mb-px transition-colors ${tab === k ? "border-[#465B7F] text-[#16202B]" : "border-transparent text-[#5B6672] hover:text-[#16202B]"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>
      <div className="p-10 pt-6">
        {tab === "empresa" && <EmpresaFolios only="empresa" />}
        {tab === "folios" && <EmpresaFolios only="folios" />}
        {tab === "panel" && <PanelSettings />}
        {tab === "bancos" && <BancosPago />}
        {tab === "agentes" && <Agentes />}
        {tab === "usuarios" && <Usuarios />}
        {tab === "bitacora" && <Bitacora />}
        {tab === "mantenimiento" && isAdmin && <Mantenimiento />}
      </div>
    </>
  );
}

function EmpresaFolios({ only }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [company, setCompany] = useState({ name: "", rfc: "", address: "", phone: "", email: "", email2: "", default_iva: 16 });
  const [folios, setFolios] = useState({ COT: 1, REM: 1, OT: 1, FAC: 1 });
  const [savedFolios, setSavedFolios] = useState({ COT: 1, REM: 1, OT: 1, FAC: 1 });
  const [prefixes, setPrefixes] = useState(DEFAULT_PREFIXES);
  const [savedPrefixes, setSavedPrefixes] = useState(DEFAULT_PREFIXES);

  const load = useCallback(() => {
    api.get("/settings").then((r) => {
      const s = r.data;
      setCompany({ ...s.company, default_iva: s.default_iva ?? 16 });
      const f = { COT: 1, REM: 1, OT: 1, FAC: 1, ...(s.folios || {}) };
      setFolios(f);
      setSavedFolios(f);
      const p = { ...DEFAULT_PREFIXES, ...(s.folio_prefixes || {}) };
      setPrefixes(p);
      setSavedPrefixes(p);
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveCompany = async (e) => {
    e.preventDefault();
    try {
      await api.put("/settings/company", { ...company, default_iva: Number(company.default_iva) || 0 });
      toast.success("Datos de empresa guardados");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const saveFolios = async (e) => {
    e.preventDefault();
    const payload = {};
    for (const k of ["COT", "REM", "OT", "FAC"]) {
      const val = Number(folios[k]) || 1;
      if (val !== Number(savedFolios[k])) payload[k] = val;
    }
    const prefixPayload = {};
    if (isAdmin) {
      for (const k of ["COT", "REM", "OT", "FAC"]) {
        if ((prefixes[k] || "") !== (savedPrefixes[k] || "")) prefixPayload[k] = prefixes[k];
      }
      if (Object.keys(prefixPayload).length) payload.prefixes = prefixPayload;
    }
    if (Object.keys(payload).length === 0) { toast.info("No hay cambios en los folios"); return; }
    try { await api.put("/settings/folios", payload); toast.success("Folios actualizados"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const setC = (k) => (e) => setCompany({ ...company, [k]: e.target.value });
  const setF = (k) => (e) => setFolios({ ...folios, [k]: e.target.value });
  const setP = (k) => (e) => setPrefixes({ ...prefixes, [k]: e.target.value });

  if (only === "empresa") return (
    <form onSubmit={saveCompany} className="border border-[#E3E8EE] bg-white p-8 max-w-2xl">
      <div className="overline mb-1">Datos de la empresa</div>
      <p className="font-mono-data text-xs text-[#5B6672] mb-6">Aparecen en cotizaciones, remisiones y facturas.</p>
      <div className="space-y-4">
        <Input label="Nombre / Razón social" value={company.name || ""} onChange={setC("name")} testid="company-name" />
        <div className="grid grid-cols-2 gap-4">
          <Input label="RFC" value={company.rfc || ""} onChange={setC("rfc")} testid="company-rfc" />
          <Input label="Teléfono" value={company.phone || ""} onChange={setC("phone")} testid="company-phone" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input label="Correo 1" value={company.email || ""} onChange={setC("email")} testid="company-email" />
          <Input label="Correo 2 (opcional)" value={company.email2 || ""} onChange={setC("email2")} testid="company-email2" />
        </div>
        <label className="block">
          <span className="overline block mb-1.5">Dirección</span>
          <textarea rows={3} value={company.address || ""} onChange={setC("address")} data-testid="company-address"
            placeholder="Calle y número, colonia, C.P., ciudad, estado"
            className="w-full bg-white border border-[#E3E8EE] px-3 py-2 font-mono-data text-sm text-[#1C1A17] focus:outline-none focus:ring-1 focus:ring-[#465B7F] focus:border-[#465B7F]" />
          <span className="font-mono-data text-xs text-[#98A2AE] mt-1 block">Puedes usar varias líneas; se respetarán en el PDF.</span>
        </label>
        <Input label="IVA por defecto (%)" type="number" step="any" value={company.default_iva} onChange={setC("default_iva")} testid="company-iva" />
      </div>
      <div className="flex justify-end mt-6"><Btn type="submit" data-testid="save-company-button">Guardar empresa</Btn></div>
    </form>
  );

  return (
    <form onSubmit={saveFolios} className="border border-[#E3E8EE] bg-white p-8 max-w-2xl">
      <div className="overline mb-1">Folios iniciales</div>
      <p className="font-mono-data text-xs text-[#5B6672] mb-6">Define con qué número inicia cada documento. Cambiarlo reinicia el consecutivo.</p>
      <div className="grid grid-cols-2 gap-4">
        <FolioField label="Cotización" prefix="COT" prefixValue={prefixes.COT} onPrefixChange={setP("COT")} editablePrefix={isAdmin} value={folios.COT} onChange={setF("COT")} />
        <FolioField label="Remisión" prefix="REM" prefixValue={prefixes.REM} onPrefixChange={setP("REM")} editablePrefix={isAdmin} value={folios.REM} onChange={setF("REM")} />
        <FolioField label="Orden (OT)" prefix="OT" prefixValue={prefixes.OT} onPrefixChange={setP("OT")} editablePrefix={isAdmin} value={folios.OT} onChange={setF("OT")} />
        <FolioField label="Factura" prefix="FAC" prefixValue={prefixes.FAC} onPrefixChange={setP("FAC")} editablePrefix={isAdmin} value={folios.FAC} onChange={setF("FAC")} />
      </div>
      <div className="flex justify-end mt-6"><Btn type="submit" data-testid="save-folios-button">Guardar folios</Btn></div>
    </form>
  );
}

function FolioField({ label, prefix, value, onChange, prefixValue, onPrefixChange, editablePrefix }) {
  return (
    <label className="block">
      <span className="overline block mb-1.5">{label}</span>
      <div className="flex items-center">
        {editablePrefix ? (
          <input
            type="text"
            value={prefixValue ?? ""}
            onChange={onPrefixChange}
            data-testid={`folio-prefix-${prefix}`}
            title="Prefijo / nomenclatura (solo Administrador)"
            className="w-24 font-mono-data text-sm bg-[#EEF2F6] border border-r-0 border-[#E3E8EE] px-2 py-2 text-[#16202B] focus:outline-none focus:ring-1 focus:ring-[#465B7F]"
          />
        ) : (
          <span className="font-mono-data text-sm bg-[#EEF2F6] border border-r-0 border-[#E3E8EE] px-2 py-2 text-[#5B6672]">{prefixValue ?? `${prefix}-`}</span>
        )}
        <input type="number" min="1" value={value} data-testid={`folio-${prefix}`} onChange={onChange}
          className="w-full bg-white border border-[#E3E8EE] px-3 py-2 font-mono-data text-sm focus:outline-none focus:ring-1 focus:ring-[#465B7F]" />
      </div>
    </label>
  );
}

function PanelSettings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [visibility, setVisibility] = useState(30);
  const [saved, setSaved] = useState(30);

  useEffect(() => {
    api.get("/settings").then((r) => {
      const v = r.data?.dashboard?.bg_visibility;
      const val = typeof v === "number" ? v : 30;
      setVisibility(val);
      setSaved(val);
    });
  }, []);

  const save = async () => {
    try {
      await api.put("/settings/dashboard", { bg_visibility: Number(visibility) });
      setSaved(Number(visibility));
      toast.success("Intensidad del fondo del panel guardada");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const overlayAlpha = Math.max(0.05, Math.min(0.95, (100 - visibility) / 100));

  return (
    <div className="border border-[#E3E8EE] bg-white p-8 max-w-2xl">
      <div className="overline mb-1">Fondo del panel</div>
      <p className="font-mono-data text-xs text-[#5B6672] mb-6">Ajusta qué tan visible se ve la imagen de fondo del Panel (Dashboard). No afecta a los demás módulos.</p>

      {/* Vista previa */}
      <div
        className="relative h-40 rounded-lg overflow-hidden border border-[#E3E8EE] mb-6"
        style={{ backgroundImage: "url(/dashboard-bg.webp)", backgroundSize: "cover", backgroundPosition: "center" }}
        data-testid="panel-bg-preview"
      >
        <div className="absolute inset-0" style={{ backgroundColor: `rgba(9,16,28,${overlayAlpha})` }} />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="bg-white/95 px-4 py-2 rounded shadow font-mono-data text-sm text-[#16202B]">Vista previa de una tarjeta</div>
        </div>
      </div>

      <label className="block">
        <div className="flex items-center justify-between mb-2">
          <span className="overline">Visibilidad de la imagen</span>
          <span className="font-mono-data text-sm font-bold text-[#16202B]" data-testid="panel-bg-value">{visibility}%</span>
        </div>
        <input
          type="range" min="0" max="100" step="1"
          value={visibility}
          onChange={(e) => setVisibility(Number(e.target.value))}
          disabled={!isAdmin}
          data-testid="panel-bg-slider"
          className="w-full accent-[#465B7F] disabled:opacity-50"
        />
        <div className="flex justify-between font-mono-data text-xs text-[#98A2AE] mt-1">
          <span>Oculta (fondo sólido)</span>
          <span>Muy visible</span>
        </div>
      </label>

      {isAdmin ? (
        <div className="flex justify-end mt-6">
          <Btn type="button" onClick={save} disabled={Number(visibility) === Number(saved)} data-testid="save-panel-bg-button">Guardar</Btn>
        </div>
      ) : (
        <p className="font-mono-data text-xs text-[#98A2AE] mt-4">Solo un Administrador puede cambiar este ajuste.</p>
      )}
    </div>
  );
}


const emptyUser = { name: "", email: "", password: "", role: "custom", permissions: {}, active: true };

function CatalogAdmin({ endpoint, prefix, description }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => api.get(`${endpoint}?all=true`).then((r) => setItems(r.data)), [endpoint]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setName(""); setEditing(null); setOpen(true); };
  const openEdit = (b) => { setName(b.name); setEditing(b.id); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Nombre requerido");
    try {
      if (editing) await api.put(`${endpoint}/${editing}`, { name });
      else await api.post(endpoint, { name });
      toast.success(editing ? "Registro actualizado" : "Registro creado");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggleActive = async (b) => {
    try {
      await api.put(`${endpoint}/${b.id}`, { active: !(b.active !== false) });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="font-mono-data text-sm text-[#5B6672]">{description}</p>
        <Btn onClick={openNew} data-testid={`new-${prefix}-button`}><Plus size={16} /> Nuevo registro</Btn>
      </div>
      <div className="border border-[#E3E8EE] bg-white data-scroll">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#E3E8EE] text-left">
              <Th>Nombre</Th><Th>Estado</Th><Th className="text-right">Acciones</Th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><Td colSpan={3} className="text-center py-8">Sin registros. Agrega el primero.</Td></tr>
            ) : items.map((b) => (
              <tr key={b.id} data-testid={`${prefix}-row-${b.id}`} className="border-b border-[#E3E8EE] last:border-0 hover:bg-[#E9EEF3]">
                <Td className={`font-medium ${b.active === false ? "text-[#98A2AE] line-through" : "text-[#16202B]"}`}>{b.name}</Td>
                <Td>{b.active === false ? "Inactivo" : "Activo"}</Td>
                <Td className="text-right whitespace-nowrap">
                  <button onClick={() => openEdit(b)} data-testid={`edit-${prefix}-${b.id}`} className="text-[#5B6672] hover:text-[#465B7F] mx-1.5"><Pencil size={15} /></button>
                  <button onClick={() => toggleActive(b)} data-testid={`toggle-${prefix}-${b.id}`}
                    className="font-mono-data text-xs uppercase tracking-wider text-[#5B6672] hover:text-[#16202B] mx-1.5">
                    {b.active === false ? "Activar" : "Desactivar"}
                  </button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Editar registro" : "Nuevo registro"}>
        <form onSubmit={save} className="space-y-4">
          <Input label="Nombre *" value={name} onChange={(e) => setName(e.target.value)} required testid={`${prefix}-name`} />
          <div className="flex justify-end gap-3">
            <Btn variant="ghost" type="button" onClick={() => setOpen(false)}>Cancelar</Btn>
            <Btn type="submit" data-testid={`save-${prefix}-button`}>Guardar</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function BancosPago() {
  return <CatalogAdmin endpoint="/payment-banks" prefix="bank" description="Catálogo de bancos y datos complementarios de pago usados en Cobranza." />;
}

function Agentes() {
  return <CatalogAdmin endpoint="/agents" prefix="agent" description="Catálogo de agentes asignables a clientes." />;
}


function Usuarios() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyUser);
  const [editing, setEditing] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(null);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptyUser); setEditing(null); setOpen(true); };
  const openEdit = (u) => { setForm({ ...emptyUser, ...u, password: "", permissions: toActionMap(u.permissions) }); setEditing(u.id); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (editing && !payload.password) delete payload.password;
      if (editing) await api.put(`/users/${editing}`, payload);
      else await api.post("/users", payload);
      toast.success(editing ? "Usuario actualizado" : "Usuario creado");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const remove = async () => {
    if (!deleteOpen) return;
    try { await api.delete(`/users/${deleteOpen.id}`); toast.success("Usuario eliminado"); setDeleteOpen(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setDeleteOpen(null); }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleAction = (mod, act) => setForm((f) => {
    const perms = { ...(f.permissions || {}) };
    const cur = new Set(perms[mod] || []);
    if (cur.has(act)) {
      cur.delete(act);
      if (act === "view") cur.clear(); // sin consultar no aplica ninguna acción
    } else {
      cur.add(act);
      if (act !== "view") cur.add("view"); // toda acción implica poder consultar
    }
    const arr = [...cur];
    if (arr.length) perms[mod] = arr; else delete perms[mod];
    return { ...f, permissions: perms };
  });
  const hasAct = (mod, act) => (form.permissions?.[mod] || []).includes(act);
  const roleValue = form.role === "admin" ? "admin" : "custom";

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Btn onClick={openNew} data-testid="new-user-button"><Plus size={16} /> Nuevo usuario</Btn>
      </div>
      <div className="border border-[#E3E8EE] bg-white data-scroll">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#E3E8EE] text-left">
              <Th>Nombre</Th><Th>Correo</Th><Th>Rol</Th><Th>Estado</Th><Th className="text-right">Acciones</Th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-[#E3E8EE] last:border-0 hover:bg-[#E9EEF3]">
                <Td className="text-[#16202B] font-medium">{u.name}</Td>
                <Td>{u.email}</Td>
                <Td>{ROLE_LABELS[u.role] || u.role}</Td>
                <Td>{u.active === false ? "Inactivo" : "Activo"}</Td>
                <Td className="text-right whitespace-nowrap">
                  <button onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`} className="text-[#5B6672] hover:text-[#465B7F] mx-1.5"><Pencil size={15} /></button>
                  <button onClick={() => setDeleteOpen(u)} data-testid={`delete-user-${u.id}`} className="text-[#5B6672] hover:text-[#ED1F26] mx-1.5"><Trash2 size={15} /></button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Editar usuario" : "Nuevo usuario"} wide>
        <form onSubmit={save} className="space-y-4">
          <Input label="Nombre *" value={form.name} onChange={set("name")} required testid="user-name" />
          <Input label="Correo *" type="email" value={form.email} onChange={set("email")} required disabled={!!editing} testid="user-email" />
          <Input label={editing ? "Nueva contraseña (opcional)" : "Contraseña *"} type="password" value={form.password} onChange={set("password")} required={!editing} testid="user-password" />
          <Select label="Tipo de acceso" value={roleValue} onChange={(e) => setForm({ ...form, role: e.target.value })} testid="user-role">
            <option value="admin">Administrador (acceso total)</option>
            <option value="custom">Personalizado (permisos por módulo)</option>
          </Select>
          {roleValue === "custom" && (
            <div className="border border-[#E3E8EE]">
              <div className="overline px-3 pt-3 pb-2">Permisos por módulo</div>
              <p className="font-mono-data text-xs text-[#5B6672] px-3 pb-3">Marca las acciones permitidas. Cualquier acción activa "Consultar" automáticamente.</p>
              <div className="data-scroll">
                <table className="w-full" data-testid="permissions-matrix">
                  <thead>
                    <tr className="border-y border-[#E3E8EE] text-left bg-[#F7F9FB]">
                      <th className="overline font-normal px-3 py-2">Módulo</th>
                      {ACTION_COLS.map((a) => (
                        <th key={a.key} className="overline font-normal px-2 py-2 text-center">{a.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {MODULES.map((m) => (
                      <tr key={m.key} data-testid={`perm-row-${m.key}`} className="border-b border-[#E3E8EE] last:border-0">
                        <td className="font-mono-data text-sm text-[#16202B] px-3 py-2 whitespace-nowrap">{m.label}</td>
                        {ACTION_COLS.map((a) => {
                          const applies = m.actions.includes(a.key);
                          return (
                            <td key={a.key} className="text-center px-2 py-2">
                              {applies ? (
                                <input type="checkbox" checked={hasAct(m.key, a.key)} onChange={() => toggleAction(m.key, a.key)}
                                  data-testid={`perm-${m.key}-${a.key}`} className="w-4 h-4 accent-[#465B7F] cursor-pointer" />
                              ) : (
                                <span className="text-[#C7CDD4]">—</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {editing && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.active !== false} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="user-active" className="w-4 h-4 accent-[#465B7F]" />
              <span className="font-mono-data text-sm">Usuario activo</span>
            </label>
          )}
          <div className="flex justify-end gap-3">
            <Btn variant="ghost" type="button" onClick={() => setOpen(false)}>Cancelar</Btn>
            <Btn type="submit" data-testid="save-user-button">Guardar</Btn>
          </div>
        </form>
      </Modal>

      <ConfirmDialog open={!!deleteOpen} onClose={() => setDeleteOpen(null)} onConfirm={remove}
        title="Eliminar usuario" message={`¿Eliminar a ${deleteOpen?.name}? Ya no podrá iniciar sesión.`}
        confirmLabel="Eliminar usuario" testid="confirm-delete-user-button" />
    </div>
  );
}

function Bitacora() {
  const [logs, setLogs] = useState([]);
  const [userName, setUserName] = useState("");
  const [df, setDf] = useState("");
  const [dt, setDt] = useState("");
  const [cleanup, setCleanup] = useState(null);
  const [purging, setPurging] = useState(false);

  const loadLogs = () => api.get("/audit").then((r) => setLogs(r.data));
  const loadCleanup = () => api.get("/audit/cleanup-status").then((r) => setCleanup(r.data)).catch(() => {});
  useEffect(() => { loadLogs(); loadCleanup(); }, []);

  const fmtDateTime = (s) => {
    if (!s) return "—";
    try { return new Date(s).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return s; }
  };

  const runCleanup = async () => {
    setPurging(true);
    try {
      const { data } = await api.post("/audit/cleanup");
      setCleanup(data);
      await loadLogs();
      toast.success(`Limpieza ejecutada: ${data.cleanup?.deleted_count ?? 0} registro(s) eliminado(s)`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo ejecutar la limpieza");
    } finally {
      setPurging(false);
    }
  };

  const names = Array.from(new Set(logs.map((l) => l.user_name).filter(Boolean)));
  const filtered = logs.filter((l) => {
    if (userName && l.user_name !== userName) return false;
    const d = (l.created_at || "").slice(0, 10);
    if (df && d < df) return false;
    if (dt && d > dt) return false;
    return true;
  });
  const pageLogs = usePagination(filtered, 50);

  const exportCsv = () => {
    const rows = [["Fecha", "Usuario", "Correo", "Acción", "Documento", "Referencia", "Detalle"]];
    filtered.forEach((l) => rows.push([l.created_at, l.user_name, l.user_email || "", l.action, l.entity, l.ref || "", l.description || ""]));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bitacora_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="emphasis-off">
      <div className="border border-[#465B7F]/25 bg-[#465B7F]/5 px-4 py-3 mb-6 flex flex-wrap items-center gap-x-6 gap-y-2" data-testid="audit-cleanup-summary">
        <div className="flex items-center gap-2">
          <Trash2 size={15} className="text-[#465B7F]" />
          <span className="font-mono-data text-xs text-[#465B7F]">
            Limpieza automática: se conservan solo los últimos <b>{cleanup?.retention_months ?? 3} meses</b> de actividad; los registros más antiguos se eliminan a diario.
          </span>
        </div>
        <div className="font-mono-data text-xs text-[#5B6672]">
          Registros actuales: <b data-testid="audit-current-count">{cleanup?.current_count ?? logs.length}</b>
        </div>
        <div className="font-mono-data text-xs text-[#5B6672]">
          Última limpieza: <b>{fmtDateTime(cleanup?.cleanup?.last_run)}</b>
          {cleanup?.cleanup && (
            <> · <b data-testid="audit-last-deleted">{cleanup.cleanup.deleted_count}</b> registro(s) eliminado(s)</>
          )}
        </div>
        <button onClick={runCleanup} disabled={purging} data-testid="audit-run-cleanup"
          className="ml-auto inline-flex items-center gap-1.5 border border-[#465B7F] text-[#465B7F] px-3 py-1.5 text-xs uppercase tracking-wider hover:bg-[#465B7F] hover:text-white transition-colors disabled:opacity-60">
          <Trash2 size={13} /> {purging ? "Limpiando…" : "Ejecutar limpieza ahora"}
        </button>
      </div>
      <div className="border border-[#E3E8EE] bg-white p-4 mb-6 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="overline">Usuario</span>
          <select value={userName} onChange={(e) => setUserName(e.target.value)} data-testid="audit-filter-user"
            className="bg-white border border-[#E3E8EE] px-2 py-1.5 font-mono-data text-sm max-w-[12rem] focus:outline-none focus:ring-1 focus:ring-[#465B7F]">
            <option value="">Todos</option>
            {names.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="overline">Desde</span>
          <input type="date" value={df} onChange={(e) => setDf(e.target.value)} data-testid="audit-filter-from"
            className="bg-white border border-[#E3E8EE] px-2 py-1.5 font-mono-data text-sm focus:outline-none focus:ring-1 focus:ring-[#465B7F]" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="overline">Hasta</span>
          <input type="date" value={dt} onChange={(e) => setDt(e.target.value)} data-testid="audit-filter-to"
            className="bg-white border border-[#E3E8EE] px-2 py-1.5 font-mono-data text-sm focus:outline-none focus:ring-1 focus:ring-[#465B7F]" />
        </label>
        <button onClick={exportCsv} data-testid="audit-export-csv"
          className="ml-auto inline-flex items-center gap-1.5 border border-[#465B7F] text-[#465B7F] px-3 py-1.5 text-xs uppercase tracking-wider hover:bg-[#465B7F] hover:text-white transition-colors">
          <Download size={13} /> Exportar CSV
        </button>
      </div>
      <div className="border border-[#E3E8EE] bg-white data-scroll">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#E3E8EE] text-left">
              <Th>Fecha</Th><Th>Usuario</Th><Th>Acción</Th><Th>Documento</Th><Th>Referencia</Th><Th>Detalle</Th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><Td className="py-8 text-center" colSpan={6}>Sin actividad registrada.</Td></tr>
            ) : pageLogs.pageItems.map((l) => (
              <tr key={l.id} data-testid={`audit-row-${l.id}`} className="border-b border-[#E3E8EE] last:border-0 hover:bg-[#E9EEF3]">
                <Td>{fmtDate(l.created_at)}</Td>
                <Td className="text-[#16202B]">{l.user_name}</Td>
                <Td>{l.action}</Td>
                <Td>{l.entity}</Td>
                <Td>{l.ref || "—"}</Td>
                <Td>{l.description || "—"}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination {...pageLogs} testid="pagination-audit" />
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`overline font-normal px-4 py-3 ${className}`}>{children}</th>;
}
function Td({ children, className = "", colSpan }) {
  return <td colSpan={colSpan} className={`font-mono-data text-sm text-[#5B6672] px-4 py-3 ${className}`}>{children}</td>;
}

function Mantenimiento() {
  const { maintenance, setMaintenance, refreshMaintenance } = useAuth();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [togglingMaint, setTogglingMaint] = useState(false);

  const toggleMaintenance = async () => {
    const next = !maintenance;
    if (next && !window.confirm("¿Activar el modo de mantenimiento? Todos los usuarios (excepto el administrador) verán la pantalla de 'Sistema en mantenimiento' y no podrán acceder a ningún módulo.")) return;
    setTogglingMaint(true);
    try {
      await api.post("/system/maintenance", { enabled: next });
      setMaintenance(next);
      await refreshMaintenance();
      toast.success(next ? "Sistema APAGADO: los demás usuarios verán la pantalla de mantenimiento" : "Sistema ENCENDIDO: acceso restablecido para todos");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo cambiar el estado del sistema");
    } finally { setTogglingMaint(false); }
  };

  const run = async (dryRun) => {
    setLoading(true);
    try {
      const r = await api.post(`/mantenimiento/limpiar-vinculos-remisiones?dry_run=${dryRun}`);
      setResult({ ...r.data });
      const n = r.data?.resumen?.cotizaciones_afectadas ?? 0;
      if (dryRun) {
        toast.info(n ? `${n} cotización(es) con vínculos a remisiones anuladas` : "Base de datos limpia: sin vínculos inconsistentes");
      } else {
        toast.success(n ? `Limpieza aplicada: ${n} cotización(es) corregida(s)` : "No había nada que limpiar");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error al ejecutar la limpieza");
    } finally { setLoading(false); }
  };

  const res = result?.resumen;
  return (
    <div className="max-w-3xl space-y-6" data-testid="mantenimiento-panel">
      <div className={`border p-8 ${maintenance ? "border-[#ED1F26]/40 bg-[#FEF2F2]" : "border-[#E3E8EE] bg-white"}`} data-testid="system-shutdown-card">
        <div className="overline mb-1">Estado del sistema</div>
        <h3 className="text-lg font-semibold text-[#16202B] mb-2">Apagado del sistema (modo mantenimiento)</h3>
        <p className="text-sm text-[#5B6672] leading-relaxed mb-4">
          Al activarlo, <b>todos los usuarios excepto el administrador</b> verán la pantalla
          <b> "Sistema en mantenimiento, por favor intente más tarde"</b> y no podrán acceder a ningún módulo.
          Tú (administrador) podrás seguir usando el sistema con normalidad.
        </p>
        <div className="flex items-center gap-4">
          <span className={`inline-flex items-center gap-2 font-mono-data text-xs uppercase tracking-wider px-3 py-1.5 border ${maintenance ? "border-[#ED1F26]/40 text-[#ED1F26] bg-white" : "border-[#49AC9A]/40 text-[#2F6B39] bg-white"}`} data-testid="system-status-indicator">
            <span className={`w-2 h-2 rounded-full ${maintenance ? "bg-[#ED1F26]" : "bg-[#49AC9A]"}`} />
            {maintenance ? "Apagado (mantenimiento)" : "Encendido (operativo)"}
          </span>
          <button
            onClick={toggleMaintenance}
            disabled={togglingMaint}
            data-testid="toggle-maintenance-button"
            className={`inline-flex items-center gap-2 px-5 py-2 text-xs uppercase tracking-wider text-white transition-colors disabled:opacity-60 ${maintenance ? "bg-[#49AC9A] hover:bg-[#3d9184]" : "bg-[#ED1F26] hover:bg-[#c81a20]"}`}
          >
            <Power size={14} /> {togglingMaint ? "Aplicando…" : (maintenance ? "Encender sistema" : "Apagar sistema")}
          </button>
        </div>
      </div>

      <div className="border border-[#E3E8EE] bg-white p-8">
        <div className="overline mb-1">Limpieza de vínculos</div>
        <h3 className="text-lg font-semibold text-[#16202B] mb-2">Depurar remisiones anuladas de las cotizaciones</h3>
        <p className="text-sm text-[#5B6672] leading-relaxed mb-4">
          Recorre todas las cotizaciones y elimina de su lista cualquier remisión que ya esté <b>anulada</b> o
          que ya no exista (por ejemplo folios anulados antes de la última actualización). Si una cotización
          queda sin remisiones activas y estaba en estatus <b>Aprobada</b>, se revierte automáticamente a
          <b> Por enviar (Borrador)</b>.
        </p>
        <div className="flex items-start gap-2 bg-[#FFF7ED] border border-[#FED7AA] text-[#9A3412] px-3 py-2 text-xs mb-5">
          <AlertTriangle size={15} className="mt-0.5 shrink-0" />
          <span>Ejecútalo en el entorno correcto. Si tu app está en producción, corre primero un <b>Redeploy</b> y luego usa este botón dentro de tu app publicada. La <b>Simulación</b> no modifica nada.</span>
        </div>
        <div className="flex flex-wrap gap-3">
          <button onClick={() => run(true)} disabled={loading} data-testid="mantenimiento-dry-run"
            className="inline-flex items-center gap-1.5 border border-[#465B7F] text-[#465B7F] px-4 py-2 text-xs uppercase tracking-wider hover:bg-[#465B7F] hover:text-white transition-colors disabled:opacity-60">
            {loading ? "Analizando…" : "Simular (sin cambios)"}
          </button>
          <button onClick={() => run(false)} disabled={loading} data-testid="mantenimiento-apply"
            className="inline-flex items-center gap-1.5 bg-[#465B7F] text-white px-4 py-2 text-xs uppercase tracking-wider hover:bg-[#3a4d6d] transition-colors disabled:opacity-60">
            <Eraser size={13} /> {loading ? "Limpiando…" : "Ejecutar limpieza"}
          </button>
        </div>

        {res && (
          <div className="mt-6 border-t border-[#E3E8EE] pt-5" data-testid="mantenimiento-result">
            <div className="text-xs uppercase tracking-wider text-[#5B6672] mb-3">
              {result.dry_run ? "Resultado de la simulación" : "Resultado de la limpieza"}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
              {[["Cotizaciones afectadas", res.cotizaciones_afectadas],
                ["Vínculos removidos", res.vinculos_removidos],
                ["Remisiones desvinculadas", res.remisiones_anuladas_desvinculadas ?? 0],
                ["Revertidas a Borrador", res.cotizaciones_revertidas_a_borrador],
                ["Total cotizaciones", res.total_cotizaciones]].map(([l, v]) => (
                <div key={l} className="border border-[#E3E8EE] bg-[#F7F9FB] p-3">
                  <div className="font-mono-data text-2xl text-[#16202B]" data-testid="mantenimiento-metric">{v}</div>
                  <div className="text-[11px] uppercase tracking-wider text-[#5B6672] mt-1">{l}</div>
                </div>
              ))}
            </div>
            {result.detalle?.length > 0 ? (
              <div className="border border-[#E3E8EE] bg-white data-scroll">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#E3E8EE] text-left">
                      <Th>Cotización</Th><Th>Estatus previo</Th><Th>Remisiones removidas</Th><Th>Activas restantes</Th><Th>Nuevo estatus</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.detalle.map((d) => (
                      <tr key={d.quote_id} className="border-b border-[#E3E8EE] last:border-0">
                        <Td className="text-[#16202B]">{d.cotizacion}</Td>
                        <Td>{d.status_previo}</Td>
                        <Td>{(d.remisiones_removidas || []).join(", ")}</Td>
                        <Td>{d.remisiones_activas_restantes}</Td>
                        <Td>{d.status_nuevo || "—"}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (res.remisiones_anuladas_desvinculadas ?? 0) > 0 ? (
              <div className="text-sm text-[#16202B] bg-[#F0F9FF] border border-[#BAE6FD] px-3 py-2">
                Se {result.dry_run ? "detectaron" : "desvincularon"} <b>{res.remisiones_anuladas_desvinculadas}</b> remisión(es) anulada(s) que aún tenían un vínculo directo (quote_id) a una cotización.
              </div>
            ) : (
              <div className="text-sm text-[#0E7C5A] bg-[#ECFDF5] border border-[#A7F3D0] px-3 py-2">
                Base de datos limpia: no se encontraron cotizaciones con vínculos a remisiones anuladas.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

